<template>
    <li
        :key="education.id"
        :value="education.id"
        class="profile-educations__list-item"
        data-testid="profile-education-item"
        :class="{'draggable-education': editMode, sample: isSample}"
    >
        <VJoyWrapper v-if="!isSample && editMode" class="action-edit" column-gap="4" justify="space-between" wrap="nowrap" item-stretch no-margin>
            <VJoyIconButton variant="ghost" size="small" icon="pen" :label="$t('general.edit')" @click="openEdit(education.id)" />
            <VJoyDialogTrigger :dialog="removeDialogRef" @click="emitRemoval(education.id)">
                <VJoyIconButton v-if="editMode" variant="ghost" size="small" icon="trash" class="toolbar-remove" :label="$t('general.delete')" />
            </VJoyDialogTrigger>
            <VJoyIcon size="xsmall" name="menu" class="draggable-handle" />
        </VJoyWrapper>
        <EducationReadOnly :education="educationItem" :class="{'profile-educations__list-item-content-edit': editMode}" />
    </li>
</template>

<script setup lang="ts">
    import {computed, toRef} from '#imports';
    import type {VJoyDialog} from '@maltjoy/core-vue';
    import {VJoyIconButton, VJoyDialogTrigger, VJoyIcon, VJoyWrapper} from '@maltjoy/core-vue';
    import EducationReadOnly from '~/components/_common/education/EducationReadOnly.vue';
    import type {EducationRequest} from '~/types/infra/EducationRequest';

    type Props = {
        modelValue: EducationRequest;
        isSample: boolean;
        isEditMode: boolean;
        removeDialogRef?: InstanceType<typeof VJoyDialog> | null;
    };

    const props = defineProps<Props>();
    type Emits = {
        (e: 'remove', id: number): void;
        (e: 'edit', id: number): void;
    };
    const emit = defineEmits<Emits>();
    const education = toRef(props, 'modelValue');
    const editMode = toRef(props, 'isEditMode');
    const educationItem = computed(() => {
        return {
            description: education.value.description,
            title: education.value.title,
            graduationYear: education.value.graduationYear,
            content: education.value.content,
        };
    });

    function emitRemoval(id: number) {
        emit('remove', id);
    }

    function openEdit(id: number) {
        emit('edit', id);
    }
</script>

<style lang="scss" scoped>
    .sample {
        opacity: 0.3;
    }

    .draggable-handle {
        cursor: grab;
        margin: var(--joy-core-spacing-3);

        // Tweak to ease the drag & drop on mobile
        @media screen and (max-width: 600px) {
            position: relative;

            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--joy-core-spacing-3));
            }
        }
    }

    .toolbar-remove :deep(.joy-icon),
    .toolbar-remove:hover :deep(.joy-icon) {
        color: var(--joy-color-error-50) !important; // I know
    }

    .action-edit {
        position: absolute;
        right: 0;

        @media screen and (max-width: 600px) {
            display: flex;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
        }
    }

    .profile-educations__list-item {
        position: relative;

        &:not(:first-child) {
            padding-top: var(--joy-core-spacing-6);
        }

        &:not(:last-child) {
            padding-bottom: var(--joy-core-spacing-6);
            border-bottom: 1px solid var(--joy-color-neutral-30);
        }

        &-content-edit {
            @media screen and (max-width: 600px) {
                justify-content: flex-start;
                min-height: 152px;
                width: 80%;
            }

            :deep(.profile-education-item__content__title) {
                width: 70%;

                @media screen and (max-width: 600px) {
                    width: 100%;
                }
            }
        }
    }
</style>
